import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core'
import { debounceTime } from 'rxjs/operators';

import { Location } from '../../../shared/models/location/location.model';
import { UPSPickUpType } from '../../../shared/enum/general-enum';
import { ManageLocationsService } from '../../services/manage-locations.service';
import { NotificationService } from '../../../shared/services/notification/notification.service';
import { NotificationType } from '../../../shared/models/notification-type';
import { ValidationService } from '../../../shared/services/validation/validation.service';
import { ZipCodeService } from '../../../shared/services/zip-code/zip-code.service';
import * as _ from 'lodash';
import { ErrorHandlerService } from '../../../shared/services/error-handler/error-handler.service';

@Component({
  selector: 'upsc-manage-location',
  templateUrl: './manage-location.component.html',
  styleUrls: ['./manage-location.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ManageLocationComponent implements OnInit {

  public locationForm: UntypedFormGroup;
  public editMode = false;
  public isGuest: boolean;
  public countriesList: any[];
  public contactId: string;
  public UPSPickupType = UPSPickUpType;
  public isZipMandatory = false;
  public isStateProvince = false;
  public isFRITUser: boolean;

  constructor(private locationService: ManageLocationsService,
    private formBuilder: UntypedFormBuilder,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private selfDialogRef: MatDialogRef<ManageLocationComponent>,
    private notificationService: NotificationService,
    private validationService: ValidationService,
    private errorHandlerService: ErrorHandlerService,
    private zipcodeservice: ZipCodeService,
    private translateService: TranslateService,
    public shippingLocation: Location) {
    this.getCountries();
  }

  ngOnInit() {
    this.shippingLocation = new Location();
    this.shippingLocation.Country = 'US';
    this.isFRITUser = this.data.isFRIT;
    this.initForm();

    if (this.data) {
      this.editMode = this.data.editMode ? this.data.editMode : false;
      if (this.editMode) {
        this.contactId = this.data.contactId ? this.data.contactId : '';
        this.editLocation(this.contactId);
      }
    }
  }

  initForm() {
    this.locationForm = this.formBuilder.group({
      NickName: ['', Validators.required],
      FirstName: ['', Validators.required],
      LastName: ['', Validators.required],
      CompanyName: [''],
      Email: ['', Validators.compose([this.validationService.emailFormatValidator()])],
      TelephoneNo: ['', Validators.compose([Validators.required, Validators.pattern('^[0-9]*$'), Validators.minLength(10)])],
      FaxNo: [''],
      StreetAddress: ['', Validators.required],
      ApartmentSuite: [''],
      AdditionalAddressInformation: ['', Validators.compose([Validators.maxLength(35)])],
      Country: [this.shippingLocation.Country, Validators.required],
      Zip: [''],
      City: ['', Validators.required],
      State: ['', Validators.required],
    });

    this.onCountryChange(this.shippingLocation.Country);
    this.monitorValueChanges();
  }

  private monitorValueChanges() {
    this.locationForm.controls.Zip.valueChanges
      .pipe(debounceTime(2000))
      .subscribe((value) => {
        if (value) {
          this.getAddressbyZipCode(value);
        }
      })
  }

  getCountries() {
    this.locationService.getCountries().subscribe(
      (countries) => {
        if (countries) {
          this.countriesList = _.sortBy(countries, ['CountryName']);
        }
        this.onCountryChange(this.shippingLocation.Country);
      },
      (error) => {
        this.notificationService.notify(
          this.errorHandlerService.getHttpErrorMessage(error),
          this.translateService.instant('CountriesLoadingError_msg'),
          NotificationType.ERROR);
      });
  }

  onCountryChange(val) {
    if (this.countriesList && this.countriesList.length > 0) {

      const countryObject = this.countriesList.filter(x => x.CountryCode === val);
      if (countryObject && countryObject.length > 0) {
        this.isZipMandatory = countryObject[0].IsPostalCodeAware;
        this.isStateProvince = countryObject[0].IsStateAware;
      }
      const zipControl = this.locationForm.controls.Zip;
      const stateControl = this.locationForm.controls.State;

      this.setControlValidation(zipControl, this.isZipMandatory);
      this.setControlValidation(stateControl, this.isStateProvince);
    }
  }

  private setControlValidation(control: AbstractControl, isRequired: boolean) {
    if (isRequired) {
      this.validationService.setFormControlValidators(control, Validators.compose([Validators.required]));
    } else {
      this.validationService.clearFormControlValidators([control]);
    }
  }


  editLocation(contactId: string) {
    this.checkGuest(contactId);
    this.locationService.getLocation(contactId).subscribe(
      (location) => {
        this.shippingLocation = location;
        this.populateForm();
      },
      (error) => {
        this.notificationService.notify(
          this.errorHandlerService.getHttpErrorMessage(error),
          this.translateService.instant('ErrorLoadingLocation_msg'),
          NotificationType.ERROR);
      });
  }

  populateForm() {
    this.locationForm.setValue({
      NickName: this.shippingLocation.NickName,
      FirstName: this.shippingLocation.FirstName,
      LastName: this.shippingLocation.LastName,
      CompanyName: this.shippingLocation.CompanyName,
      Email: this.shippingLocation.Email,
      TelephoneNo: this.shippingLocation.TelephoneNo,
      FaxNo: this.shippingLocation.FaxNo,
      StreetAddress: this.shippingLocation.StreetAddress,
      ApartmentSuite: this.shippingLocation.ApartmentSuite,
      AdditionalAddressInformation: this.shippingLocation.AdditionalAddressInformation,
      Country: this.shippingLocation.Country,
      Zip: this.shippingLocation.Zip,
      City: this.shippingLocation.City,
      State: this.shippingLocation.State ? this.shippingLocation.State : this.shippingLocation.ProvinceRegion,
    });
    this.onCountryChange(this.shippingLocation.Country);
    this.monitorValueChanges();
  }

  updateLocation() {
    if (this.locationForm.valid) {
      this.mapFormValuetoShippingLocation();
      this.locationService.saveLocation(this.shippingLocation).subscribe(
        (response) => {
          this.notificationService.notify('', this.translateService.instant('LocationUpdatedInfo'), NotificationType.SUCCESS);
          this.selfDialogRef.close({ result: 'success' });
        },
        (error) => {
          this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(error),
            this.translateService.instant('ErrorUpdatingLocation_msg'),
            NotificationType.ERROR);
        });
    }
  }

  mapFormValuetoShippingLocation() {
    this.shippingLocation.NickName = this.locationForm.controls['NickName'].value;
    this.shippingLocation.FirstName = this.locationForm.controls['FirstName'].value;
    this.shippingLocation.LastName = this.locationForm.controls['LastName'].value;
    this.shippingLocation.CompanyName = this.locationForm.controls['CompanyName'].value;
    this.shippingLocation.Email = this.locationForm.controls['Email'].value;
    this.shippingLocation.TelephoneNo = this.locationForm.controls['TelephoneNo'].value;
    this.shippingLocation.FaxNo = this.locationForm.controls['FaxNo'].value;
    this.shippingLocation.StreetAddress = this.locationForm.controls['StreetAddress'].value;
    this.shippingLocation.ApartmentSuite = this.locationForm.controls['ApartmentSuite'].value;
    this.shippingLocation.AdditionalAddressInformation = this.locationForm.controls['AdditionalAddressInformation'].value;
    this.shippingLocation.Country = this.locationForm.controls['Country'].value;
    this.shippingLocation.Zip = this.locationForm.controls['Zip'].value;
    this.shippingLocation.City = this.locationForm.controls['City'].value;
    this.shippingLocation.State = this.locationForm.controls['State'].value;
    this.shippingLocation.ProvinceRegion = this.locationForm.controls['State'].value;
  }

  saveLocation() {
    if (this.locationForm.valid) {
      this.mapFormValuetoShippingLocation();
      this.locationService.saveLocation(this.shippingLocation).subscribe(
        (response) => {
          this.notificationService.notify('', this.translateService.instant('LocationAddedInfo'), NotificationType.SUCCESS);
          this.selfDialogRef.close({ result: 'success' });
        },
        (error) => {
          this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(error),
            this.translateService.instant('ErrorAddingLocation_msg'),
            NotificationType.ERROR);
        });
    }
  }

  checkGuest(contactId: string) {
    this.locationService.checkGuestLocation(contactId).subscribe(
      (response) => {
        this.isGuest = response;
      },
      (error) => {
        this.notificationService.notify(
          this.errorHandlerService.getHttpErrorMessage(error),
          this.translateService.instant('ErrorCheckingGuest_msg'),
          NotificationType.ERROR);
      })
  }

  getAddressbyZipCode(event) {
    if (!['US', 'CA', 'PR', 'VI', 'GU'].includes(this.locationForm.controls.Country.value)) {
      return;
    }

    this.zipcodeservice.getAddressByZipCode(this.locationForm.controls.Zip.value)
      .subscribe(
        (response) => {
          if (response) {
            this.locationForm.controls['City'].setValue(response.City);
            this.locationForm.controls['State'].setValue(response.State);
            this.locationForm.controls['Country'].setValue(response.Country);
          }
        },
        (error) => {
          this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(error),
            this.translateService.instant('ErrorLoadingAddress_msg'),
            NotificationType.ERROR);
        });
  }
}
